/**
 * useGltfClone — load a GLB and return a per-instance scene clone.
 *
 * Wraps `useGLTF` from `@react-three/drei` (which already handles Suspense
 * + global caching) and clones the scene so multiple `<XtsModule>`s of the
 * same type don't share mutated transforms.
 *
 * Per-clone post-processing (cheap, runs once when the GLB joins the scene):
 *   - `castShadow = receiveShadow = true` on every mesh. Three.js only
 *     runs shadow passes when the WebGLRenderer's `shadowMap.enabled` is
 *     true (controlled by `<Canvas shadows>` at the component root), so
 *     these flags cost nothing when shadows are off.
 *   - Anisotropic filtering on every texture, bumped to the renderer's
 *     hardware max (typically 16). Crisper texels at grazing camera
 *     angles — meaningful for the long aluminium-extruded module rails
 *     viewed from a 3/4 perspective. Negligible per-frame cost; pure
 *     sampler state.
 *   - `envMapIntensity` defaults to 1.0 on PBR materials so they pick up
 *     `scene.environment` (set by `<SceneEnvironment>`). Glb authors who
 *     want a different default still win — we only touch materials whose
 *     intensity matches the three.js default of 1.
 *
 * For an empty URL, returns `null` so the caller can render a placeholder.
 */

import { useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { Mesh, Texture } from 'three';
import type { Material, MeshStandardMaterial, WebGLRenderer } from 'three';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { GLTF, GLTFLoader } from 'three-stdlib';
import { configureLoader } from '../../assets/configureGltfLoaders.js';

/**
 * Build the `extendLoader` callback drei's `useGLTF` runs against the
 * underlying GLTFLoader. drei already wires Draco + Meshopt; we add the KTX2
 * texture loader (needs the renderer for GPU-format detection). Exported as a
 * pure factory so the wiring can be unit-tested without rendering.
 */
export function makeExtendLoader(
  gl: WebGLRenderer | null | undefined,
): (loader: GLTFLoader) => void {
  return (loader: GLTFLoader) => {
    configureLoader(loader, { gl });
  };
}

const TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
] as const;

export function useGltfClone(url: string): GLTF | null {
  // Skip the entire load when no URL.
  if (!url) {
    return null;
  }
  // useGLTF caches the parsed GLTF globally. We only ever read its scene
  // here; cloning preserves skinned meshes correctly via SkeletonUtils.
  // `extendLoader` attaches the KTX2 texture loader so Basis-compressed
  // textures decode; drei wires Draco + Meshopt itself.
  const gl = useThree((s) => s.gl);
  const gltf = useGLTF(
    url,
    true,
    true,
    makeExtendLoader(gl),
  ) as GLTF;
  const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
  return useMemo(() => {
    const cloned = cloneSkeleton(gltf.scene);
    cloned.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const mats = Array.isArray(mesh.material)
        ? mesh.material
        : mesh.material
          ? [mesh.material]
          : [];
      for (const m of mats) {
        applyMaterialPbrTuning(m, maxAnisotropy);
      }
    });
    return { ...gltf, scene: cloned } as GLTF;
  }, [gltf, maxAnisotropy]);
}

function applyMaterialPbrTuning(material: Material, maxAnisotropy: number): void {
  // Bump anisotropy on every bound texture. Pure sampler state — no
  // re-upload, no rebuild.
  for (const key of TEXTURE_KEYS) {
    const tex = (material as unknown as Record<string, unknown>)[key];
    if (tex && (tex as Texture).isTexture) {
      const t = tex as Texture;
      if (t.anisotropy < maxAnisotropy) t.anisotropy = maxAnisotropy;
    }
  }
  // PBR materials with the three.js-default envMapIntensity of 1 inherit
  // `scene.environment` automatically; we just leave them. Authoring
  // pipelines that exported a non-1 value win — don't override.
  const std = material as Partial<MeshStandardMaterial>;
  if (typeof std.envMapIntensity === 'number' && std.envMapIntensity === 0) {
    // GLBs occasionally export envMapIntensity = 0, which mutes IBL
    // entirely. Bump to the default so the PBR pipeline behaves.
    std.envMapIntensity = 1;
  }
}
